import { createSlice } from "@reduxjs/toolkit";
import { v5 as uuidv5 } from "uuid";

import { INITIAL_MSGS, checkTranslation, dotLoader } from "utils";

const NAMESPACE = "123e4567-e89b-12d3-a456-426614174000";

const generateMessageUUID = (msg) => {
  const createdAt = msg.createdAt ? new Date(msg.createdAt).toISOString() : new Date().toISOString();
  const uniqueString = `${msg.speaker}-${createdAt}-${msg.message}`.toLowerCase().replace(/\s+/g, "-");

  return uuidv5(uniqueString, NAMESPACE);
};

const loadMessagesFromLocalStorage = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const property_id = urlParams.get("property_id");

  const savedMessages = localStorage.getItem(`${property_id}-messages`);
  const meMessages = [];
  let isMessages = null;

  try {
    isMessages = JSON.parse(savedMessages);
  } catch (e) {
    isMessages = null;
  }

  if (isMessages && isMessages?.length) {
    // Drop leftover loader bubbles, but never typed text.
    isMessages = isMessages?.filter((v) => v.isUserTyped || (v.message !== "typing" && !v.message?.includes("Typing") && !v.message?.includes("bounce-dot")));

    meMessages.push(...isMessages);
  }

  return meMessages.length > 0 ? meMessages : null;
};

const saveMessagesToLocalStorage = (messages) => {
  if (!messages || !Array.isArray(messages)) {
    return;
  }

  const urlParams = new URLSearchParams(window.location.search);
  const property_id = urlParams.get("property_id");

  messages = messages?.slice(-200);

  messages = messages?.map((msg) => ({
    ...msg,
    id: msg.id || generateMessageUUID(msg),
    speaker: msg.speaker || "bot",
    createdAt: msg.createdAt || new Date().toISOString(),
  }));

  localStorage.setItem(`${property_id}-messages`, JSON.stringify(messages));
};

const initialState = {
  messages: INITIAL_MSGS,
  stopScroll: false,
  isFaq: false,
  showAllOptions: false,
  changeLangAlert: false,
  // AMA validation: amaInputError drives the red border, amaAlertVisible the header alert, and
  // amaAlertSeq restarts the alert timer. We store the error type, not translated text.
  amaInputError: null,
  amaAlertVisible: false,
  amaAlertSeq: 0,
  faqLoader: false,
  transStatus: "pending",
  // Flipped by loadInitialMessages. Guards writers that would otherwise persist the pristine
  // INITIAL_MSGS over a transcript still sitting in localStorage — refreshGreeting runs from a
  // mount effect and would do exactly that.
  initialized: false,
  pagination: { page: 1, per_page: 20, loadMore: true, lastResLength: 0, loadedFromStorage: false },
};

export const messagesSlice = createSlice({
  name: "messages",
  initialState,
  reducers: {
    resetMessages: (state) => {
      state = initialState;
    },
    setPagination: (state, action) => {
      state.pagination = action.payload || { page: 1, per_page: 20, loadMore: true, lastResLength: 0 };
    },
    setFaqLoader: (state, action) => {
      state.faqLoader = action.payload;
    },
    setShowAllOptions: (state, action) => {
      state.showAllOptions = action.payload;
    },
    setTransStatus: (state, action) => {
      state.transStatus = action.payload;
    },
    formatMessages: (state, action) => {
      const rawMessages = action.payload.length > 0 && action.payload[0].type === "translated" ? action.payload : checkTranslation(action.payload);

      const newMessages = rawMessages.map((msg) => ({
        ...msg,
        id: msg.id || generateMessageUUID(msg),
        createdAt: msg.createdAt || new Date().toISOString(),
      }));

      state.messages = newMessages;
      saveMessagesToLocalStorage(state.messages);
    },

    updateFeedbackMessages: (state) => {
      state.messages = state.messages.map((msg) =>
        msg.subject === "positive_feedback" || msg.subject === "negative_feedback" ? { ...msg, subject: "" } : msg
      );

      saveMessagesToLocalStorage(state.messages);
    },

    updateContactMessages: (state) => {
      state.messages = state.messages.map((msg) => (msg.subject === "contact us" || msg.subject === "unsatisfactory_answer" ? { ...msg, subject: "" } : msg));

      saveMessagesToLocalStorage(state.messages);
    },

    addNewMessage: (state, action) => {
      // const newMessages = checkTranslation(action.payload);

      const newMessages = checkTranslation(action.payload).map((msg) => ({
        ...msg,
        id: msg.id || generateMessageUUID(msg),
        createdAt: msg.createdAt || new Date().toISOString(),
      }));

      state.messages = [...state.messages, ...newMessages];
      state.pagination.page = 1;

      if (state.pagination.loadedFromStorage) {
        state.pagination.loadedFromStorage = false;
      }

      saveMessagesToLocalStorage(state.messages);
    },
    updateMessage: (state, action) => {
      const { id, feedback } = action.payload;

      const arr = state.messages.map((v) => (v.id === id ? { ...v, feedback } : v));
      state.messages = arr;
      saveMessagesToLocalStorage(state.messages);
    },

    loadInitialMessages: (state, action) => {
      const page = action?.payload?.page || "";
      const savedMessages = loadMessagesFromLocalStorage();
      const contactOnly = page == "contact-me";
      const unitsOnly = page == "available-units";
      const feedbackOnly = page == "feedback";
      const feeCalculatorOnly = page == "fee-calculator";

      let newMessages = [];

      if (contactOnly) {
        const trans = checkTranslation([{ message: "fewMoreDetailGet", speaker: "bot", createdAt: new Date().toISOString() }]);
        newMessages = trans;
      } else if (unitsOnly) {
        const loaderMsg = `<div class="hayley-inline-block">${`Loading Units ${dotLoader}`}</div>`;
        const trans = checkTranslation([{ message: loaderMsg, speaker: "bot", createdAt: new Date().toISOString() }]);
        newMessages = trans;
      } else if (feedbackOnly) {
        newMessages = [];
      } else if (feeCalculatorOnly) {
        newMessages = [];
      } else if (savedMessages?.length > 1) {
        const lastMessage = savedMessages?.[savedMessages.length - 1];

        if (lastMessage && lastMessage.isInitMessage) {
          savedMessages[savedMessages.length - 1].date = new Date().toISOString();
          newMessages = [...savedMessages];
          state.pagination.loadedFromStorage = true;
        } else {
          const trans = checkTranslation(INITIAL_MSGS)?.[0];
          const updatedMessages = [...savedMessages, { ...trans, date: new Date().toISOString() }];
          newMessages = updatedMessages;
        }
      } else {
        newMessages = checkTranslation(INITIAL_MSGS);
      }

      // Only a TRAILING greeting belongs to this load; any earlier one is history and must stay
      // frozen, so refreshGreeting can never reach back and rewrite it.
      const lastIndex = (newMessages?.length || 0) - 1;

      state.messages = newMessages?.map((msg, index) => ({
        ...msg,
        ...(msg.isInitMessage ? { greetingActive: index === lastIndex } : {}),
        id: msg.id || generateMessageUUID(msg),
      }));

      state.pagination.page = 1;
      state.initialized = true;
      saveMessagesToLocalStorage(state.messages);
    },

    restartMessagesWithLastOnly: (state) => {
      const savedMessages = loadMessagesFromLocalStorage();

      if (savedMessages?.length) {
        const lastMessage = savedMessages[savedMessages.length - 1];

        if (lastMessage) {
          const newMessages = [lastMessage];
          state.messages = newMessages;
          saveMessagesToLocalStorage(newMessages);
        }
      }
    },

    moveToTop: (state) => {
      state.pagination.loadedFromStorage = true;
    },

    // Writes the opening bubble for THIS load.
    //
    // A greeting already in the transcript is history and is left exactly as it was — same words,
    // same language. Only the trailing one is replaced, so reloading on a transcript that already
    // ends in a greeting does not stack two of them back to back; reloading mid-conversation
    // appends a fresh greeting underneath and leaves the earlier one alone.
    //
    // Older greetings are marked inactive so refreshGreeting can never reach back and rewrite
    // them — that is the behaviour QA rejected, a Spanish welcome turning English under the
    // visitor after a toggle.
    upsertGreeting: (state, action) => {
      const greeting = action.payload;

      const last = state.messages[state.messages.length - 1];
      const kept = last?.isInitMessage ? state.messages.slice(0, -1) : state.messages;

      state.messages = [
        ...kept.map((msg) => (msg.isInitMessage ? { ...msg, greetingActive: false } : msg)),
        {
          ...greeting,
          greetingActive: true,
          id: greeting.id || generateMessageUUID(greeting),
          createdAt: greeting.createdAt || new Date().toISOString(),
        },
      ];

      state.pagination.page = 1;

      if (state.pagination.loadedFromStorage) {
        state.pagination.loadedFromStorage = false;
      }

      saveMessagesToLocalStorage(state.messages);
    },

    // Marks every greeting as history. Dispatched the instant the VISITOR changes language —
    // the header toggle and the AMA auto-detect — so the bubble they have already read keeps its
    // wording and its language for good. Dispatched synchronously in those handlers, so a
    // refresh already in flight cannot beat it.
    freezeGreeting: (state) => {
      let changed = false;

      state.messages = state.messages.map((msg) => {
        if (!msg.isInitMessage || !msg.greetingActive) {
          return msg;
        }

        changed = true;

        return { ...msg, greetingActive: false };
      });

      if (changed) {
        saveMessagesToLocalStorage(state.messages);
      }
    },

    // Finishes resolving the greeting written for THIS load, once its inputs arrive — the chatbot
    // title (which lands a few hundred ms after the bubble is written) or the remote translation
    // bundle. Messages are stored as resolved prose, so a bubble cannot re-resolve itself.
    //
    // Two guards keep this from touching history:
    //   • greetingActive — only the newest greeting, never an earlier one, and freezeGreeting
    //     clears it the moment the VISITOR changes language. That is the distinction that
    //     matters: the app correcting its own boot guess must still finish resolving the
    //     bubble, but a sentence the visitor has already read must never be rewritten
    //     underneath them — which is what QA rejected.
    //
    // The id is preserved so the bubble is not remounted.
    refreshGreeting: (state, action) => {
      const { message } = action.payload;

      if (!message) {
        return;
      }

      let changed = false;

      state.messages = state.messages.map((msg) => {
        if (!msg.isInitMessage || !msg.greetingActive || msg.message === message) {
          return msg;
        }

        changed = true;

        return { ...msg, message };
      });

      if (changed) {
        saveMessagesToLocalStorage(state.messages);
      }
    },

    removeAndAddMessages: (state, action) => {
      // const newMessages = checkTranslation(action.payload);
      const newMessages = checkTranslation(action.payload).map((msg) => ({
        ...msg,
        id: msg.id || generateMessageUUID(msg),
        createdAt: msg.createdAt || new Date().toISOString(),
      }));

      state.messages = [...state.messages.slice(0, -1), ...newMessages];
      state.pagination.page = 1;

      if (state.pagination.loadedFromStorage) {
        state.pagination.loadedFromStorage = false;
      }

      saveMessagesToLocalStorage(state.messages);
    },
    removeAndAddNoTransMessages: (state, action) => {
      state.messages = [...state.messages.slice(0, -1), ...action.payload];
      saveMessagesToLocalStorage(state.messages);
    },
    updateScroll: (state, action) => {
      state.stopScroll = action.payload;
    },
    updateFaq: (state, action) => {
      state.isFaq = action.payload;
    },
    updateLangAlert: (state, action) => {
      state.changeLangAlert = action.payload;
    },
    // Show the error and restart the alert timer.
    setAmaInputError: (state, action) => {
      state.amaInputError = action.payload;
      state.amaAlertVisible = true;
      state.amaAlertSeq = (state.amaAlertSeq || 0) + 1;
    },
    // Hide the alert only; the red border stays.
    hideAmaAlert: (state) => {
      state.amaAlertVisible = false;
    },
    // Clear both the red border and the alert.
    clearAmaInputError: (state) => {
      state.amaInputError = null;
      state.amaAlertVisible = false;
    },
    setLoadedFromStorage: (state, action) => {
      state.pagination.loadedFromStorage = action.payload;
    },
    filterEditMessages: (state, action) => {
      const filteredEditMsg = state.messages?.map((m) => {
        const { optionsType, ...rest } = m;

        return rest;
      });

      // const newMessages = checkTranslation(action.payload);
      const newMessages = checkTranslation(action.payload).map((msg) => ({
        ...msg,
        id: msg.id || generateMessageUUID(msg),
        createdAt: msg.createdAt || new Date().toISOString(),
      }));

      state.messages = [...filteredEditMsg, ...newMessages];
      saveMessagesToLocalStorage(state.messages);
    },
    removeLoadingMsgs: (state) => {
      // Remove loader bubbles, but keep typed text that looks like one.
      state.messages = state.messages?.filter(
        (v) => v.isUserTyped || (v.message !== "typing" && !v.message.includes("Typing") && !v.message.includes("bounce-dot"))
      );
    },

    restartChatMessages: (state, action) => {
      const welcomeMessage = action.payload;

      const newMessage = {
        ...welcomeMessage,
        // Active: this is the greeting for the restarted conversation, so a chatbot_title still
        // in flight must still be able to land on it. Without this the bubble was frozen at
        // whatever was in the store the instant Restart was clicked — which, right after a
        // language switch, is the previous locale's title.
        greetingActive: true,
        id: welcomeMessage?.id || generateMessageUUID(welcomeMessage),
        createdAt: welcomeMessage.createdAt || new Date().toISOString(),
      };

      state.messages = [newMessage];
      state.amaInputError = null;
      state.amaAlertVisible = false;

      state.pagination = {
        page: 1,
        per_page: 20,
        loadMore: true,
        lastResLength: 0,
        loadedFromStorage: false,
      };

      saveMessagesToLocalStorage(state.messages);
    },
  },
});

export const {
  moveToTop,
  formatMessages,
  addNewMessage,
  removeLoadingMsgs,
  filterEditMessages,
  removeAndAddNoTransMessages,
  updateMessage,
  removeAndAddMessages,
  upsertGreeting,
  refreshGreeting,
  freezeGreeting,
  updateScroll,
  updateFaq,
  setPagination,
  setTransStatus,
  setFaqLoader,
  setShowAllOptions,
  updateLangAlert,
  setAmaInputError,
  hideAmaAlert,
  clearAmaInputError,
  resetMessages,
  loadInitialMessages,
  restartMessagesWithLastOnly,
  restartChatMessages,
  setLoadedFromStorage,
  updateFeedbackMessages,
  updateContactMessages,
} = messagesSlice.actions;
export const selectMessages = (state) => state.messages.messages;
export const stopScroll = (state) => state.messages.stopScroll;
export const selectIsFaq = (state) => state.messages.isFaq;
export const selectTransStatus = (state) => state.messages.transStatus;
export const selectLangAlert = (state) => state.messages.changeLangAlert;
// Defaults keep stores without these fields (older state, tests) working.
export const selectAmaInputError = (state) => state.messages.amaInputError ?? null;
export const selectAmaAlertVisible = (state) => !!state.messages.amaAlertVisible;
export const selectAmaAlertSeq = (state) => state.messages.amaAlertSeq ?? 0;
export const selectFaqLoader = (state) => state.messages.faqLoader;
export const getShowAllOptions = (state) => state.messages.showAllOptions;
export const chatPagination = (state) => state.messages.pagination;
export const selectMessagesInitialized = (state) => state.messages.initialized;

export default messagesSlice.reducer;
